"use client";

import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { AeraButton } from "@/app/components/ui/AeraButton";

type IconLike = LucideIcon;

export function CreditsPlanCard(props: {
  title: ReactNode;
  credits: ReactNode;
  price: ReactNode;
  Icon: IconLike;
  selected?: boolean;
  badgeLabel?: ReactNode;
  showPerMonth?: boolean;
  fixedWidth?: boolean;
  onChoose?: () => void;
  chooseLabel?: ReactNode;
  busy?: boolean;
  disabled?: boolean;
  ButtonIcon?: IconLike;
}) {
  const {
    title,
    credits,
    price,
    Icon,
    selected = false,
    badgeLabel,
    showPerMonth = true,
    fixedWidth = true,
    onChoose,
    chooseLabel = "เลือกแพ็กเกจ",
    busy = false,
    disabled = false,
    ButtonIcon,
  } = props;

  const EffectiveButtonIcon = ButtonIcon ?? Icon;
  const isCurrentPackage =
    badgeLabel === "แพ็กเกจปัจจุบัน" ||
    (typeof badgeLabel === "string" && badgeLabel.includes("แพ็กเกจปัจจุบัน"));
  const isRecommended =
    badgeLabel === "แนะนำ" ||
    (typeof badgeLabel === "string" && badgeLabel.includes("แนะนำ"));
  const isHighlighted = isCurrentPackage || isRecommended || selected;
  const isButtonDisabled = disabled || isCurrentPackage;

  return (
    <article
      className={`relative flex w-full shrink-0 flex-col items-center rounded-2xl border p-5 pt-7 text-center transition-shadow ${
        fixedWidth ? "md:w-[320px]" : ""
      } ${
        isCurrentPackage || isRecommended
          ? "border-aera-deeppurple/60 bg-aera-deeppurple/10"
          : "border-aera-lightgray/80 bg-aera-white"
      }`}
    >
      {badgeLabel ? (
        <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 rounded-full bg-aera-deeppurple px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-aera-white">
          {badgeLabel}
        </span>
      ) : null}

      <div
        className={`mb-3 flex h-14 w-14 items-center justify-center rounded-xl ring-1 ${
          isCurrentPackage || isRecommended
            ? "bg-aera-deeppurple/10 ring-aera-deeppurple/35"
            : "bg-aera-amberlight/30 ring-aera-amber/15"
        }`}
      >
        <Icon
          className={`h-7 w-7  ${
            isCurrentPackage || isRecommended
              ? "text-aera-deeppurple"
              : "text-aera-amberdark"
          }`}
          strokeWidth={1.75}
          aria-hidden
        />
      </div>

      <span className="text-base font-bold uppercase text-aera-charcoal">
        {title}
      </span>

      <span className="mt-1 tabular-nums text-xl font-medium text-aera-gray">
        {credits} เครดิต
      </span>

      <span
        className={`mt-1 text-3xl font-bold md:text-2xl lg:text-3xl ${
          isCurrentPackage || isRecommended
            ? "text-aera-deeppurple"
            : "text-aera-amberdark"
        }`}
      >
        ฿{price}
        {showPerMonth ? (
          <span className="text-lg font-medium text-aera-gray">/เดือน</span>
        ) : null}
      </span>

      {onChoose ? (
        <AeraButton
          variant="secondary"
          fullWidth
          fontWeight="medium"
          disabled={isButtonDisabled}
          loading={busy}
          onClick={onChoose}
          icon={
            !busy ? (
              <EffectiveButtonIcon className="h-4 w-4" strokeWidth={1.75} />
            ) : undefined
          }
          className="mt-4"
        >
          {chooseLabel}
        </AeraButton>
      ) : null}
    </article>
  );
}

export function CreditsTopupCard(props: {
  label: ReactNode;
  credits: ReactNode;
  price: ReactNode;
  Icon: IconLike;
  selected?: boolean;
  fixedWidth?: boolean;
  onChoose?: () => void;
  chooseLabel?: ReactNode;
  busy?: boolean;
  disabled?: boolean;
  ButtonIcon?: IconLike;
}) {
  const {
    label,
    credits,
    price,
    Icon,
    fixedWidth = true,
    onChoose,
    chooseLabel = "เลือกแพ็กเกจ",
    busy = false,
    disabled = false,
    ButtonIcon,
  } = props;

  const EffectiveButtonIcon = ButtonIcon ?? Icon;

  return (
    <article
      className={`relative w-full shrink-0 rounded-xl border border-aera-lightgray/80 bg-aera-white/90 px-4 py-4 ${
        fixedWidth ? "md:w-[320px]" : ""
      }`}
    >
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-aera-amberlight/25 ring-1 ring-aera-amber/15">
          <Icon
            className="h-6 w-6 text-aera-amberdark"
            strokeWidth={1.75}
            aria-hidden
          />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-sm font-bold uppercase text-aera-charcoal">
              แพ็ก {label}
            </span>
            <span className="tabular-nums text-base font-bold text-aera-amberdark">
              ฿{price}
            </span>
          </div>
          <p className="mt-0.5 tabular-nums text-sm font-medium text-aera-gray">
            {credits} เครดิต
          </p>
        </div>
      </div>

      {onChoose ? (
        <AeraButton
          variant="secondary"
          fullWidth
          fontWeight="medium"
          disabled={disabled}
          loading={busy}
          onClick={onChoose}
          icon={
            !busy ? (
              <EffectiveButtonIcon className="h-4 w-4" strokeWidth={1.75} />
            ) : undefined
          }
          className="mt-4"
        >
          {chooseLabel}
        </AeraButton>
      ) : null}
    </article>
  );
}
